class HeroLayers extends HTMLElement {
  constructor() {
    super();
    this.rafId = null;
    this.inView = false;
    this.scrollQueued = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    this.mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    this.scrollProgress = 0;
    this.gap = 120;
    this.drag = {
      active: false,
      index: -1,
      pointerId: null,
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
    };
    this.layerDrag = [];
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.settleMouse = this.settleMouse.bind(this);
  }

  connectedCallback() {
    this.stack = this.querySelector('[data-hero-layers-stack]');
    this.layers = Array.from(this.querySelectorAll('[data-hero-layers-layer]'));
    this.section = this.closest('.shopify-section') || this;

    if (!this.stack || this.layers.length === 0) return;

    this.enableFloat = this.dataset.enableFloat !== 'false';
    this.enableMouse = this.dataset.enableMouse !== 'false' && !this.isTouch;
    this.enableScrollExit = this.dataset.enableScrollExit !== 'false';
    // Drag is expensive; keep off unless explicitly needed later.
    this.enableDrag = false;
    this.floatAmplitude = parseFloat(this.dataset.floatAmplitude || '10');
    this.mouseIntensity = parseFloat(this.dataset.mouseIntensity || '12');
    this.scrollSpread = parseFloat(this.dataset.scrollSpread || '80');
    this.scrollExitDistance = parseFloat(this.dataset.scrollExitDistance || '240');
    this.layerDrag = this.layers.map(() => ({ x: 0, y: 0, tx: 0, ty: 0 }));
    this.readGap();
    this.applyBasePositions();

    if (this.reducedMotion) {
      this.classList.add('is-reduced');
      this.section.classList.add('is-reduced');
      return;
    }

    if (this.enableFloat) {
      this.classList.add('has-float');
      this.style.setProperty('--hero-layers-float', `${this.floatAmplitude}px`);
      this.section.style.setProperty('--hero-layers-float', `${this.floatAmplitude}px`);
    }

    if (this.enableScrollExit) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('touchmove', this.onScroll, { passive: true });
      this.updateScrollExit();
    }

    if (this.enableMouse) {
      window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    }

    window.addEventListener('resize', this.onResize, { passive: true });
    this.observeVisibility();
  }

  disconnectedCallback() {
    this.stopSettle();
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('touchmove', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    this.layers.forEach((layer) => {
      layer.removeEventListener('pointerdown', this.onPointerDown);
    });
    if (this.io) this.io.disconnect();
  }

  onResize() {
    this.readGap();
    this.applyBasePositions();
    if (this.enableScrollExit) this.updateScrollExit();
  }

  // O CSS usa clamp()/calc() pra deixar o gap fluido (ver 'fluid-value' no
  // Liquid) — mas getComputedStyle numa CUSTOM property devolve esse texto
  // cru, sem resolver ('clamp(...)'), então parseFloat dá NaN. Em vez de
  // reimplementar a fórmula aqui (o que pode divergir do que o CSS realmente
  // renderiza, ex: dentro do iframe do preview do editor, que nem sempre
  // reporta a mesma largura de uma aba de navegador comum), lemos o `width`
  // computado de um elemento-sonda que usa var(--hero-layers-gap) como
  // width — uma propriedade normal, que resolve clamp()/calc() de verdade.
  readGap() {
    const probe = this.querySelector('[data-hero-layers-gap-probe]');
    const measured = probe ? parseFloat(getComputedStyle(probe).width) : NaN;

    if (Number.isFinite(measured)) {
      this.gap = measured;
      return;
    }

    const min = parseFloat(this.dataset.gapMobile);
    const max = parseFloat(this.dataset.gapDesktop);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      this.gap = 120;
      return;
    }

    const minVw = 375;
    const maxVw = 1440;
    const vw = Math.min(Math.max(window.innerWidth, minVw), maxVw);
    const t = (vw - minVw) / (maxVw - minVw);
    this.gap = min + (max - min) * t;
  }

  baseYForDepth(depth) {
    if (depth >= 3) return -this.gap;
    if (depth <= 1) return this.gap;
    return 0;
  }

  applyBasePositions() {
    this.layers.forEach((layer, index) => {
      const depth = parseFloat(layer.dataset.depth || String(3 - index));
      const baseY = this.baseYForDepth(depth);
      layer.style.setProperty('--layer-base-y', `${baseY}px`);
      layer.style.setProperty('--parallax-x', '0px');
      layer.style.setProperty('--parallax-y', '0px');
      layer.style.setProperty('--scroll-y', '0px');
      layer.style.setProperty('--drag-x', '0px');
      layer.style.setProperty('--drag-y', '0px');
    });
  }

  observeVisibility() {
    if (typeof IntersectionObserver === 'undefined') {
      this.inView = true;
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.inView = entry.isIntersecting;
          this.classList.toggle('is-inview', entry.isIntersecting);
          if (!entry.isIntersecting) this.stopSettle();
        });
      },
      { threshold: 0.01, rootMargin: '80px 0px 80px 0px' }
    );
    this.io.observe(this.section);

    const rect = this.section.getBoundingClientRect();
    this.inView = rect.bottom > 0 && rect.top < window.innerHeight;
    this.classList.toggle('is-inview', this.inView);
  }

  onScroll() {
    if (!this.inView || this.scrollQueued) return;
    this.scrollQueued = true;
    requestAnimationFrame(() => {
      this.scrollQueued = false;
      this.updateScrollExit();
    });
  }

  updateScrollExit() {
    if (!this.enableScrollExit) return;
    const rect = this.section.getBoundingClientRect();
    const traveled = Math.max(0, -rect.top);
    this.scrollProgress = Math.min(1, traveled / Math.max(this.scrollExitDistance, 1));
    const scroll = this.scrollProgress;
    const exitSpread = scroll * this.scrollSpread;

    this.stack.style.opacity = String(1 - scroll);
    this.stack.style.webkitTransform = `scale(${1 - scroll * 0.12})`;
    this.stack.style.transform = `scale(${1 - scroll * 0.12})`;

    this.layers.forEach((layer, index) => {
      const depth = parseFloat(layer.dataset.depth || String(3 - index));
      layer.style.setProperty('--scroll-y', `${(2 - depth) * exitSpread}px`);
    });
  }

  onPointerMove(event) {
    if (!this.inView || !this.enableMouse || this.drag.active) return;

    const x = (event.clientX / window.innerWidth - 0.5) * 2;
    const y = (event.clientY / window.innerHeight - 0.5) * 2;
    this.mouse.tx = Math.max(-1, Math.min(1, x));
    this.mouse.ty = Math.max(-1, Math.min(1, y));
    this.startSettle();
  }

  startSettle() {
    if (this.rafId) return;
    this.rafId = requestAnimationFrame(this.settleMouse);
  }

  stopSettle() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  settleMouse() {
    this.mouse.x += (this.mouse.tx - this.mouse.x) * 0.12;
    this.mouse.y += (this.mouse.ty - this.mouse.y) * 0.12;

    this.layers.forEach((layer, index) => {
      const depth = parseFloat(layer.dataset.depth || String(3 - index));
      const depthFactor = depth / 3;
      const px = this.mouse.x * this.mouseIntensity * depthFactor;
      const py = this.mouse.y * this.mouseIntensity * 0.45 * depthFactor;
      layer.style.setProperty('--parallax-x', `${px}px`);
      layer.style.setProperty('--parallax-y', `${py}px`);
    });

    const done =
      Math.abs(this.mouse.tx - this.mouse.x) < 0.01 &&
      Math.abs(this.mouse.ty - this.mouse.y) < 0.01;

    if (done) {
      this.rafId = null;
      return;
    }

    this.rafId = requestAnimationFrame(this.settleMouse);
  }

  // Kept for API compatibility if drag is re-enabled later.
  onPointerDown() {}
  onPointerUp() {}
  magnetClamp(value) {
    return value;
  }
}

if (!customElements.get('hero-layers')) {
  customElements.define('hero-layers', HeroLayers);
}

// Indicador de rolagem: clique rola até a próxima seção da página.
(function () {
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-hero-scroll-cue]').forEach(function (button) {
    if (reducedMotion) button.setAttribute('data-reduced-motion', '');

    button.addEventListener('click', function () {
      var section = button.closest('.shopify-section');
      var next = section ? section.nextElementSibling : null;
      while (next && next.offsetParent === null) {
        next = next.nextElementSibling;
      }
      if (next) {
        next.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
      }
    });
  });
})();
